import React from 'react';
import PropTypes from 'prop-types';
import { TooltipWrapperSup } from './Tooltips';
import footnoteConstants from '../constants/footnotes';

const localizedText = footnoteConstants['en-US'];

const TableHeader = ({ title, titleLink, thStyle }) => (
  <thead>
    <tr>
      <th rowSpan='2' className={`${thStyle} px250`}>
        <a href={titleLink} target='_blank' rel='noopener noreferrer'>
          {title}<br />
          Investment Options
          <TooltipWrapperSup
            sup='i'
            placement='right'
            title='Open link for more details.'
          />
        </a>
      </th>
      <th rowSpan='2' className={`${thStyle} px70`}>
        Ticker Symbol
      </th>
      <th rowSpan='2' className={`${thStyle} px70`}>
        Latest<br />
        Month
      </th>
      <th rowSpan='2' className={`${thStyle} px70`}>
        Latest<br />
        Three<br />
        Months
      </th>
      <th rowSpan='2' className={`${thStyle} px70`}>
        Year to<br />
        Date <TooltipWrapperSup sup='1' placement='right' title={localizedText[0]} />
      </th>
      <th rowSpan='2' className={`${thStyle} px70`}>
        One<br />
        Year
      </th>
      <th colSpan='4' className='text-center'>
        Average Annualized Return <TooltipWrapperSup sup='2' placement='left' title={localizedText[1]} />
      </th>
      <th rowSpan='2' className={thStyle}>
        Inception<br />
        Date <TooltipWrapperSup sup='3' placement='left' title={localizedText[2]} />
      </th>
    </tr>
    <tr>
      <th className={`${thStyle} px70 secondTh`}>
        Three<br />
        Year
      </th>
      <th className={`${thStyle} px70 secondTh`}>
        Five<br />
        Year
      </th>
      <th className={`${thStyle} px70 secondTh`}>
        Ten<br />
        Year
      </th>
      <th className={`${thStyle} px70 secondTh`}>
        Since<br />
        Inception
      </th>
    </tr>
  </thead>
);

TableHeader.propTypes = {
  title: PropTypes.string.isRequired,
  thStyle: PropTypes.string,
  titleLink: PropTypes.string.isRequired,
};

const TargetEnrollmentTable = ({ selectedMonth, titleLink }) => {
  return (
    <table className='table stand-alone table-striped'>
      <TableHeader title='Target-Enrollment Date' thStyle='text-center remove-top-padding' titleLink={titleLink} />
      <tbody>
        {selectedMonth.targetEnrollment.map(option => (
          <tr key={option.Name} className='striped'>
            <td className='px250'>{option.Name}</td>
            <td className='text-center px70'>{option.Ticker}</td>
            <td className='text-center px70'>{option.OneMonth}</td>
            <td className='text-center px70'>{option.ThreeMonth}</td>
            <td className='text-center px70'>{option.YTD}</td>
            <td className='text-center px70'>{option.OneYear}</td>
            <td className='text-center px70'>{option.ThreeYear}</td>
            <td className='text-center px70'>{option.FiveYear}</td>
            <td className='text-center px70'>{option.TenYear}</td>
            <td className='text-center px70'>{option.SinceInception}</td>
            <td className='text-center'>{option.InceptionDate}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

TargetEnrollmentTable.propTypes = {
  selectedMonth: PropTypes.object.isRequired,
  titleLink: PropTypes.string,
};

const CustomizedRows = ({ selectedMonth }) => {
  return selectedMonth.customized.map(option => (
    <tr key={`${option.Name}-row`} className='striped'>
      <td className='px250'>
        {option.Name}
      </td>
      <td className='text-center'>{option.Ticker}</td>
      <td className='text-center'>{option.OneMonth}</td>
      <td className='text-center'>{option.ThreeMonth}</td>
      <td className='text-center'>{option.YTD}</td>
      <td className='text-center'>{option.OneYear}</td>
      <td className='text-center'>{option.ThreeYear}</td>
      <td className='text-center'>{option.FiveYear}</td>
      <td className='text-center'>{option.TenYear}</td>
      <td className='text-center'>{option.SinceInception}</td>
      <td className='text-center'>{option.InceptionDate}</td>
    </tr>
  ));
};

const CustomizedTable = ({ selectedMonth, titleLink }) => {
  const septDate = new Date('09-30-2021');
  const isSeptSelected = selectedMonth.Date.getTime() === septDate.getTime();
  return (
    <table id='customized-table' className='table stand-alone table-striped'>
      <TableHeader title='Customized' thStyle='text-center remove-top-padding outline-th' titleLink={titleLink} />
      <tbody>
        <CustomizedRows selectedMonth={selectedMonth} />
      </tbody>
      <tfoot>
        <tr>
          <td colSpan='11' className='table-definition'>The information above shows the returns for the following
            underlying investments and are net of the my529 Administrative Asset Fee. Returns on an account
            invested in the Customized Age-Based or Customized Static investment options will depend upon the
            underlying investment allocation chosen by the account owner/agent. In addition, individual account
            performance will vary based on the timing of the investments in the investment option, any cash flow
            in or out of the my529 account during the investment period, and on the balances in the my529
            accounts.
          </td>
        </tr>
        {/* Temp language for a mistake on the Sept 2021 Stable Value fund return %, should be removed in Dec 2021 */}
        {isSeptSelected &&
          <tr>
            <td colSpan='11' className='table-definition'>
              *A pricing error by the stable value fund custodian resulted in September 2021 performance
              for the Static Stable Value and Customized my529 Stable Value investment options
              to be overstated and has subsequently been corrected.
            </td>
          </tr>}
      </tfoot>
    </table>
  );
};

CustomizedTable.propTypes = {
  selectedMonth: PropTypes.object.isRequired,
  titleLink: PropTypes.string,
};

const StaticTable = ({ selectedMonth, titleLink }) => {
  return (
    <table
      className='table stand-alone table-striped page-break'
      style={{ pageBreakInside: 'avoid !important', pageBreakAfter: 'always !important' }}
    >
      <TableHeader title='Static' thStyle='text-center remove-top-padding outline-th' titleLink={titleLink} />
      <tbody>
        {selectedMonth.static.map(option => (
          <tr key={option.Name} className='striped'>
            <td className='px250'>{option.Name}</td>
            <td className='text-center px70'>{option.Ticker}</td>
            <td className='text-center px70'>{option.OneMonth}</td>
            <td className='text-center px70'>{option.ThreeMonth}</td>
            <td className='text-center px70'>{option.YTD}</td>
            <td className='text-center px70'>{option.OneYear}</td>
            <td className='text-center px70'>{option.ThreeYear}</td>
            <td className='text-center px70'>{option.FiveYear}</td>
            <td className='text-center px70'>{option.TenYear}</td>
            <td className='text-center px70'>{option.SinceInception}</td>
            <td className='text-center'>{option.InceptionDate}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

StaticTable.propTypes = {
  selectedMonth: PropTypes.object.isRequired,
  titleLink: PropTypes.string,
};

const FundUnderlyingRateInformation = ({ FDICRate, FDICYield, StableValueYield }) => {
  const notAvailable = <span style={{ color: 'red' }}>Not Available</span>;
  return (
    <div
      style={{
        border: '1px solid black',
        marginBottom: '1em',
        boxShadow: '0px 1px 8px 0px rgba(0, 0, 0, 0.2), 0px 3px 4px 0px rgba(0, 0, 0, 0.14), 0px 3px 3px -2px rgba(0, 0, 0, 0.12)'
      }}
    >
      <div
        style={{
          marginBottom: '1em',
          backgroundColor: '#41AD49',
          fontSize: '110%',
          fontWeight: 'bold',
          color: '#FFFFFF',
          height: '56px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >Current Rates/Yields</div>
      <div style={{ padding: '0 0.5em 0.5em 0.5em' }}>
        <div>Non-Mutual Fund Underlying rate information. The yields shown are not inclusive of the my529 Administrative Asset Fee, which may vary by investment option:</div>
        <br />
        <h4>
          FDIC-insured
          <TooltipWrapperSup
            sup='i'
            placement='right'
            title='The FDIC yield resets monthly.'
          />
          :
        </h4>
        <div>Annual Percentage Rate: {FDICRate ? FDICRate : notAvailable}</div>
        <div>Annual Percentage Yield: {FDICYield ? FDICYield : notAvailable}</div>
        <br />
        <h4>
          Stable Value
          <TooltipWrapperSup
            sup='i'
            placement='right'
            title='The Stable Value yield resets quarterly.'
          />
          :
        </h4>
        <div>Annual Percentage Yield: {StableValueYield ? StableValueYield : notAvailable}</div>


      </div>

    </div>
  );
};

FundUnderlyingRateInformation.propTypes = {
  FDICRate: PropTypes.string,
  FDICYield: PropTypes.string,
  StableValueYield: PropTypes.string,
};

export {
  TargetEnrollmentTable,
  StaticTable,
  CustomizedTable,
  FundUnderlyingRateInformation
};
